import { buildSort, goPromise } from "@delanobgt/admin-core/util/helpers";
import {
  ApiClientFactory,
  IMeta,
  IPagination,
  ISort,
} from "@delanobgt/admin-core/util/types";
import { CancelTokenSource } from "axios";
import _ from "lodash";

import {
  EProductCategoryGroupSortField,
  IProductCategoryGroup,
  PProductCategoryGroup,
  PProductCategoryGroupFilter,
} from "../entities";
import IProductCategoryGroupRepository from "../repository";

export default class ProductCategoryGroupRepository
  implements IProductCategoryGroupRepository<CancelTokenSource> {
  private apiClientFactory: ApiClientFactory;

  // api routes
  private PRODUCT_CATEGORY_GROUP_ROUTE = "/admin/product-category-group";

  constructor(apiClientFactory: ApiClientFactory) {
    this.apiClientFactory = apiClientFactory;
  }

  buildFilter = (filter: PProductCategoryGroupFilter) => {
    return filter;
  };

  private getSignalToken(signal: CancelTokenSource) {
    return _.get(signal, "token", null);
  }

  private getError(error: any) {
    return _.get(error, "response.data.errors", "Something went wrong.");
  }

  async getProductCategoryGroup(
    pagination: IPagination,
    filter: PProductCategoryGroupFilter,
    sorts: ISort<EProductCategoryGroupSortField>[],
    signal?: CancelTokenSource
  ): Promise<[IProductCategoryGroup[], IMeta]> {
    const sort = buildSort(sorts);
    const params = _.pickBy(
      {
        ...pagination,
        ...this.buildFilter(filter),
      },
      (val) => val
    );
    const [err, response] = await goPromise(
      this.apiClientFactory({ cancelToken: this.getSignalToken(signal) }).get(
        `${this.PRODUCT_CATEGORY_GROUP_ROUTE}?sort=${sort}`,
        {
          params,
        }
      )
    );
    if (err) {
      throw this.getError(err);
    }
    const { data, meta } = response.data;
    return [data, meta];
  }

  async getProductCategoryGroupById(
    id: number
  ): Promise<IProductCategoryGroup> {
    const [err, response] = await goPromise(
      this.apiClientFactory().get(`${this.PRODUCT_CATEGORY_GROUP_ROUTE}/${id}`)
    );
    if (err) {
      throw this.getError(err);
    }
    return response.data;
  }

  async createProductCategoryGroup(
    productCategoryGroup: PProductCategoryGroup,
    image: any
  ): Promise<void> {
    const formData = new FormData();
    formData.append("name", productCategoryGroup.name);
    formData.append("slug", productCategoryGroup.slug);
    formData.append("image", image);

    const [err] = await goPromise(
      this.apiClientFactory().post(
        `${this.PRODUCT_CATEGORY_GROUP_ROUTE}`,
        formData
      )
    );
    if (err) {
      throw this.getError(err);
    }
  }

  async updateProductCategoryGroup(
    id: number,
    productCategoryGroup: PProductCategoryGroup,
    image: any
  ): Promise<void> {
    const formData = new FormData();
    if (productCategoryGroup.name)
      formData.append("name", productCategoryGroup.name);
    if (productCategoryGroup.slug)
      formData.append("slug", productCategoryGroup.slug);
    if (image) formData.append("image", image);

    const [err] = await goPromise(
      this.apiClientFactory().patch(
        `${this.PRODUCT_CATEGORY_GROUP_ROUTE}/${id}`,
        formData
      )
    );
    if (err) {
      throw this.getError(err);
    }
  }
}
